import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, SimpleChanges, ViewChildren, OnChanges } from '@angular/core';
import { MatAccordion } from '@angular/material/expansion';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileInfo, FileUploadError, FileUploadStatus } from '@vendasta/galaxy/uploader';
import { IAMService, User } from '@vendasta/iam';
import {
  Attachment,
  CustomerApprovalEventBounced,
  CustomerApprovalEventClicked,
  CustomerApprovalEventDeferred,
  CustomerApprovalEventDelivered,
  CustomerApprovalEventDropped,
  CustomerApprovalEventOpened,
  CustomerApprovalEventProcessing,
  CustomerApprovalEventSending,
  CustomerApprovalEventSpamReport,
  CustomerApprovalEventUnsubscribed,
  CustomerApprovalEventUpdatedAnswers,
  CustomerNotesMetadata,
  DeclineCancellationEvent,
  DeclinedOrderMetadata,
  HistoryMetadata,
  NotesMetadata,
  Order,
  Origin,
  RequestCancellationEvent,
  RequestCancellationMetadata,
  SalesOrdersService,
  UserInterface as SalesUser,
  Status,
  UpdateContractDurationEvent,
  UpdateContractDurationMetadata,
  UpdatedCustomerNotesEvent,
  UpdateEnforceContractTermEvent,
  UpdatedDraftEvent,
  UpdatedLineItemsEvent,
  UpdatedNotesEvent,
  UpdatedRequestedActivationEvent,
  UpdatedRequestedActivationMetadata,
  UpdatedSalespersonEvent,
  UpdatedTagsEvent,
  UpdatedTagsMetadata,
  getOfferExpiry,
} from '@vendasta/sales-orders';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { StatusToBadgeMapping } from '../conversion-utils';
import { OrderDetailAgreement } from './order-detail-agreement';
import { OrderDetailField } from './order-detail-field';
import { ContractDurationEnumToKeys } from './sales-order-details-contract-duration/sales-contract-duration';

interface SubmitterBadgeInfo {
  color: string;
  tranlationKey: string;
}

@Component({
  selector: 'sales-ui-sales-order-details-card',
  templateUrl: 'sales-order-details-card.component.html',
  styleUrls: ['../../shared/shared-styles.scss', 'sales-order-details-card.component.scss'],
  standalone: false,
})
export class SalesOrderDetailsCardComponent implements OnInit, OnChanges {
  @Input() order: Order;
  @Input() editable: boolean;
  @Input() orderDetails: OrderDetailField[];
  @Input() isLoading = false;
  @Input() hasError = false;
  @Input() orderDetailsEditable = true;
  @Input() isCustomerDetails = false;
  /** @deprecated: this should only be used for older orders (before March 1st 2020 that don't have their decline reason in the metadata */
  @Input() declinedReason?: string;
  @Input() attachments?: Attachment[];
  @Input() agreements?: OrderDetailAgreement[];
  offerExpiryDisplay: string;
  attachmentsUploadUrl: string;
  files: FileInfo[] = [];
  isEditing$$ = new BehaviorSubject<boolean>(false);
  salesperson: User;
  @ViewChildren(MatAccordion) private readonly matAccordions;

  constructor(
    private readonly translateService: TranslateService,
    private readonly alertService: SnackbarService,
    private readonly salesOrderService: SalesOrdersService,
    private readonly userLookupService: IAMService,
  ) {}

  ngOnInit(): void {
    this.attachmentsUploadUrl = this.salesOrderService.getUploadFileUrl();
    this.files = (this.attachments || []).map((attachment) => {
      return { name: attachment.name, url: attachment.url, status: FileUploadStatus.Success };
    });

    const offerExpiryDate = getOfferExpiry(this.order);
    if (offerExpiryDate) {
      const formattedDate = formatDateWithTime(offerExpiryDate);
      if (moment(offerExpiryDate).isAfter(moment())) {
        this.offerExpiryDisplay = this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.OFFER_WILL_EXPIRE', {
          date: formattedDate,
        });
      } else {
        this.offerExpiryDisplay = this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.OFFER_EXPIRED', {
          date: formattedDate,
        });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.order && changes.order.currentValue.salespersonId != this.salesperson?.userId) {
      if (changes.order.currentValue.salespersonId) {
        this.userLookupService
          .getMultiUsers([{ userId: changes.order.currentValue.salespersonId }])
          .pipe(
            map((users) => {
              if (!!users && users.length == 1) {
                return users[0];
              }
              return null;
            }),
            take(1),
          )
          .subscribe(
            (user) => {
              if (user) {
                this.salesperson = user;
              }
            },
            (err) => {
              console.error(err);
            },
          );
      } else {
        this.salesperson = null;
      }
    }
  }

  getOrderSubmitterName(): string {
    if (!!this.orderDetails && this.orderDetails.length > 0) {
      return getDisplayNameForSalesUser(this.orderDetails[0].user);
    }
    return '';
  }

  buildCustomerApprovalNameAndDate(): string {
    const customerApproval = this.getLastCustomerApproval();
    if (!customerApproval) {
      return '';
    }
    const lastAccepted = this.translateService.instant('FRONTEND.SALES_UI.LAST_ACCEPTED');
    return lastAccepted + ' ' + this.formatDateAndNamePair(customerApproval.date, customerApproval.user);
  }

  getLastCustomerApproval(): OrderDetailField {
    if (!this.orderDetails) {
      return null;
    }

    // Clone the order details so reverse doesn't break the original array
    const orderDetailsCopy = [...this.orderDetails];
    orderDetailsCopy.reverse();

    // Get the matching history item if at least two entries exist and one is approved by the customer
    return orderDetailsCopy.find(
      (historyItem, index, statusHistory) =>
        index < statusHistory.length - 1 &&
        !historyItem.status &&
        statusHistory[index + 1].status &&
        statusHistory[index + 1].status === Status.SUBMITTED_FOR_CUSTOMER_APPROVAL,
    );
  }

  getOrderSubmitterBadgeInfo(): SubmitterBadgeInfo {
    switch (this.order.origin || Origin.SSC) {
      case Origin.SSC:
        return { color: 'purple', tranlationKey: 'FRONTEND.SALES_UI.SALESPERSON' };
      case Origin.VBC:
        return { color: 'green', tranlationKey: 'FRONTEND.SALES_UI.CLIENT' };
      case Origin.PC:
        return { color: 'blue', tranlationKey: 'FRONTEND.SALES_UI.ADMIN' };
      default:
        return null;
    }
  }

  buildDetailName(orderDetailField: OrderDetailField, isChild: boolean): string {
    if (isChild) {
      return getEventNameTranslationKeyForOrderDetailField(orderDetailField);
    }
    // Not all sales histories were created with a status
    return StatusToBadgeMapping.get(orderDetailField.status)?.text || StatusToBadgeMapping.get(Status.SUBMITTED).text;
  }

  buildDetailValue(orderDetailField: OrderDetailField): string {
    const nameAndDateString = this.formatDateAndNamePair(orderDetailField.date, orderDetailField.user);
    return this.buildValueFromMetadata(nameAndDateString, orderDetailField.metadata);
  }

  public signalToOpenAllDropdowns(): void {
    this.matAccordions.forEach((accordion) => accordion.openAll());
  }

  updateAttachmentsOnOrder(): void {
    const attachments = this.files.map((file) => {
      return {
        name: file.name,
        url: file.url,
      };
    });
    this.salesOrderService
      .updateAttachments(this.order.orderId, this.order.businessId, attachments)
      .pipe(
        take(1),
        tap(() => this.isEditing$$.next(false)),
      )
      .subscribe({
        next: () => {
          this.alertService.openSuccessSnack('FRONTEND.SALES_UI.ORDER_DETAILS.ATTACHMENTS_UPDATED_SUCCESSFULLY');
        },
        error: () => {
          this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_ATTACHMENT_GENERIC');
        },
      });
  }

  updateFilesUploaded(files: FileInfo[]): void {
    this.files = files;
  }

  handleFileUploadError(err: FileUploadError): void {
    let errorMessage = 'FRONTEND.SALES_UI.ERRORS.ERROR_ATTACHMENT_GENERIC';
    switch (err.error.message) {
      case 'GALAXY.UPLOADER.ERROR.MAX_FILE_SIZE_EXCEEDED':
        errorMessage = 'FRONTEND.SALES_UI.ERRORS.MAX_FILE_SIZE_EXCEEDED';
        break;
      case 'GALAXY.UPLOADER.ERROR.MAX_FILES_EXCEEDED':
        errorMessage = 'FRONTEND.SALES_UI.ERRORS.MAX_FILES_EXCEEDED';
        break;
      case 'GALAXY.UPLOADER.ERROR.DUPLICATE_FILE_ATTEMPT':
        errorMessage = 'FRONTEND.SALES_UI.ERRORS.DUPLICATE_FILE_ATTEMPT';
        break;
    }
    this.alertService.openErrorSnack(errorMessage);
  }

  edit(): void {
    this.isEditing$$.next(true);
  }

  private formatDateAndNamePair(date: Date, user: SalesUser): string {
    const displayName = getDisplayNameForSalesUser(user);

    const formattedDate = formatDateWithTime(date);
    if (displayName) {
      return this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.DATE_BY_USER', {
        date: formattedDate,
        user: displayName,
      });
    }
    // If there's no user just display the date ()
    return formattedDate;
  }

  private buildValueFromMetadata(nameAndDateString: string, metadata: HistoryMetadata): string {
    if (
      metadata instanceof RequestCancellationMetadata ||
      metadata instanceof NotesMetadata ||
      metadata instanceof CustomerNotesMetadata
    ) {
      let notes = metadata.current;
      if (metadata.event.id === DeclineCancellationEvent) {
        notes = metadata.event.name + '\n' + metadata.current;
      }
      return nameAndDateString + '\n' + notes;
    }
    if (metadata instanceof DeclinedOrderMetadata) {
      const notes = [...metadata.declinedReasons, metadata.current].join('\n');
      return nameAndDateString + '\n' + notes;
    }
    if (metadata instanceof UpdatedTagsMetadata) {
      const tagsString =
        !!metadata.tags && metadata.tags.length > 0
          ? this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
              value: metadata.tags.join(', '),
            })
          : this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.ALL_TAGS_REMOVED');
      return nameAndDateString + '\n' + tagsString;
    }
    if (metadata instanceof UpdatedRequestedActivationMetadata) {
      if (!!metadata.previous && !!metadata.current) {
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: formatDate(metadata.previous),
            toValue: formatDate(metadata.current),
          })
        );
      }
      if (metadata.current) {
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
            value: formatDate(metadata.current),
          })
        );
      }
      if (metadata.previous) {
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: formatDate(metadata.previous),
            toValue: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_START_DATE'),
          })
        );
      }
      return (
        nameAndDateString +
        '\n' +
        this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
          value: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_START_DATE'),
        })
      );
    }
    if (metadata instanceof UpdateContractDurationMetadata) {
      if (!!metadata.previous && !!metadata.current) {
        const previousDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.previous.duration],
        );
        const previousDuration = `${metadata.previous.value} ${previousDurationPeriod}`;
        const currentDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.current.duration],
        );
        const currentDuration = `${metadata.current.value} ${currentDurationPeriod}`;
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: previousDuration,
            toValue: currentDuration,
          })
        );
      }
      if (metadata.current) {
        const currentDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.current.duration],
        );
        const currentDuration = `${metadata.current.value} ${currentDurationPeriod}`;
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
            value: currentDuration,
          })
        );
      }
      if (metadata.previous) {
        const previousDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.previous.duration],
        );
        const previousDuration = `${metadata.previous.value} ${previousDurationPeriod}`;
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: previousDuration,
            toValue: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_LENGTH'),
          })
        );
      }
      return (
        nameAndDateString +
        '\n' +
        this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
          value: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_LENGTH'),
        })
      );
    }
    return nameAndDateString;
  }
}

function formatDate(date: Date): string {
  return new DatePipe('en-US').transform(date, 'MMMM d, y');
}

function formatDateWithTime(date: Date): string {
  return new DatePipe('en-US').transform(date, 'MMMM d, y, h:mm:ss a');
}

function getDisplayNameForSalesUser(user: SalesUser): string {
  if (!user) {
    return '';
  }
  const names = [];
  if (user.firstName) {
    names.push(user.firstName);
  }
  if (user.lastName) {
    names.push(user.lastName);
  }
  const displayName = names.join(' ');
  if (!displayName) {
    return user.email;
  }
  return displayName;
}

function getEventNameTranslationKeyForOrderDetailField(orderDetailField: OrderDetailField): string {
  if (!orderDetailField || !orderDetailField.metadata) {
    return null;
  }

  if (orderDetailField.metadata.event.id === UpdatedTagsEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_TAGS';
  }

  if (orderDetailField.metadata.event.id === UpdatedNotesEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_NOTES';
  }

  if (orderDetailField.metadata.event.id === UpdatedSalespersonEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_SALESPERSON';
  }

  if (orderDetailField.metadata.event.id === UpdatedCustomerNotesEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_CUSTOMER_NOTES';
  }

  if (orderDetailField.metadata.event.id === UpdatedRequestedActivationEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_CONTRACT_START';
  }

  if (orderDetailField.metadata.event.id === UpdateContractDurationEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_CONTRACT_LENGTH';
  }

  if (orderDetailField.metadata.event.id === UpdateEnforceContractTermEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_ENFORCE_CONTRACT_TERM';
  }

  if (orderDetailField.metadata.event.id === RequestCancellationEvent) {
    return 'FRONTEND.SALES_UI.CANCELLATION_REQUESTED';
  }

  if (orderDetailField.metadata.event.id === DeclineCancellationEvent) {
    return 'FRONTEND.SALES_UI.CANCELLATION_DECLINED';
  }

  if (orderDetailField.metadata.event.id === UpdatedDraftEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_DRAFT';
  }

  if (orderDetailField.metadata.event.id === UpdatedLineItemsEvent) {
    return 'FRONTEND.SALES_UI.UPDATED_CONTENTS';
  }

  if (orderDetailField.status !== Status.SUBMITTED_FOR_CUSTOMER_APPROVAL) {
    return null;
  }
  return getCustomerApprovalTranslationKeyForEventID(orderDetailField.metadata.event.id);
}

function getCustomerApprovalTranslationKeyForEventID(eventID: string): string {
  switch (eventID) {
    case CustomerApprovalEventSending:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.SENDING';
    case CustomerApprovalEventProcessing:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.PROCESSING';
    case CustomerApprovalEventDelivered:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DELIVERED';
    case CustomerApprovalEventBounced:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.BOUNCED';
    case CustomerApprovalEventDropped:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DROPPED';
    case CustomerApprovalEventUnsubscribed:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UNSUBSCRIBED';
    case CustomerApprovalEventDeferred:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DEFERRED';
    case CustomerApprovalEventSpamReport:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.SPAM_REPORT';
    case CustomerApprovalEventOpened:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.EMAIL_OPENED';
    case CustomerApprovalEventClicked:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.EMAIL_CLICKED';
    case CustomerApprovalEventUpdatedAnswers:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UPDATED_ANSWERS';
    default:
      return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UNKNOWN';
  }
}
